import React, {
  useCallback, useContext, useEffect, useMemo,
} from 'react';
import { IconButton } from '@material-ui/core';
import {
  Search as PageviewIcon,
  Delete as DeleteIcon,
} from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import useOperationController from '../../../../../utils/hooks/store/operation/useOperationController';
import QCXSimpleConsultPageTemplate from '../../../../../templates/simple-consult-page/QCXSimpleConsultPageTemplate';
import {
  activateByIdAsync,
  changeToConsultMode,
  changeToCreateMode,
  fetchAllAsync,
  inactivateByIdAsync,
  resetBackgroundMode,
  resetMode,
  resetModel,
  resetSubMode,
  selectModelosFollowUp,
} from '../../../../../features/modelo-follow-up/modeloFollowUpSlice';
import { isAlternativeLoadingStatus } from '../../../../../utils/store/store-utils';
import ModeloFollowUpContext from '../../../../../contexts/registrations/modelo-follow-up/ModeloFollowUpContext';
import { resetList as resetEtapaList } from '../../../../../features/etapa/etapaSlice';
import { getAnaliseDesempenhoLabelByValue } from '../../../../../shared-components/select-analise-desempenho-modulo/QCXSelectAnaliseDesempenhoModuloAutocomplete';
import { getTipoModeloFollowUpLabelByValue } from '../../../../../shared-components/select-tipo-modelo-follow-up/QCXSelectTipoModeloFollowUpAutocomplete';
import { userHasDeleteRole } from '../../../../../components/perfil/perfil-utils';

export default function ModeloFollowUpConsultPage({ authInfo = {} }) {
  const { t } = useTranslation();

  const history = useHistory();

  const {
    status,
  } = useContext(ModeloFollowUpContext);

  const dispatch = useDispatch();

  const modelosFollowUp = useSelector(selectModelosFollowUp);

  const isAlternativeLoading = useMemo(() => (
    isAlternativeLoadingStatus(status)
  ), [status]);

  const fetchAll = useCallback(() => {
    dispatch(fetchAllAsync());
  }, []);

  const resetModes = useCallback(() => {
    dispatch(resetMode());
    dispatch(resetBackgroundMode());
    dispatch(resetSubMode());
  }, []);

  const resetModels = useCallback(() => {
    dispatch(resetModel());
  }, []);

  const resetDependencies = useCallback(() => {
    dispatch(resetEtapaList());
  }, []);

  useEffect(() => {
    fetchAll();
    resetModes();
    resetModels();
    resetDependencies();
  }, []);

  const handleActivateInactivate = useOperationController(
    async (event, id) => {
      event.preventDefault();

      const { active } = modelosFollowUp.find(
        (modeloFollowUp) => modeloFollowUp.id === id
      );

      const isActivation = !active;

      if (isActivation) {
        dispatch(activateByIdAsync(id));
      } else {
        dispatch(inactivateByIdAsync(id));
      }
    }
  );

  const handleClickOnButtonNew = useCallback((event) => {
    event.stopPropagation();
    dispatch(changeToCreateMode());
  }, []);

  const handleClickOnEditButton = useCallback((event, id) => {
    event.stopPropagation();
    dispatch(changeToConsultMode());
    history.push(t('com.muralis.qcx.url.moduloGeraisModeloFollowUpDetalhes', { id }));
  }, [history]);

  const columns = [
    {
      field: 'id',
      headerAlign: 'center',
      align: 'center',
      headerName: t('com.muralis.qcx.codigo'),
      type: 'string',
      flex: 90,
      valueGetter: ({ row }) => (
        row?.id
      ),
    },
    {
      field: 'sigla',
      headerName: t('com.muralis.qcx.sigla'),
      headerAlign: 'center',
      align: 'center',
      flex: 90,
      valueGetter: ({ row }) => (
        row?.sigla || '-'
      ),
    },
    {
      field: 'description',
      headerName: t('com.muralis.qcx.nome'),
      flex: 200,
      valueGetter: ({ row }) => (
        row?.description
      ),
    },
    {
      field: 'analiseDesempenho',
      headerName: t('com.muralis.qcx.analiseDesempenho'),
      headerAlign: 'center',
      align: 'center',
      flex: 220,
      valueFormatter: ({ row }) => (
        getAnaliseDesempenhoLabelByValue(row?.analiseDesempenho) || '-'
      ),
      valueGetter: ({ row }) => (
        getAnaliseDesempenhoLabelByValue(row?.analiseDesempenho) || '-'
      ),
    },
    {
      field: 'tipo',
      headerName: t('com.muralis.qcx.tipo'),
      headerAlign: 'center',
      align: 'center',
      type: 'string',
      flex: 100,
      valueFormatter: ({ row }) => (
        getTipoModeloFollowUpLabelByValue(row?.tipo) || '-'
      ),
      valueGetter: ({ row }) => (
        getTipoModeloFollowUpLabelByValue(row?.tipo) || '-'
      ),
    },
    {
      field: 'actions',
      headerName: t('com.muralis.qcx.acoes.label'),
      headerAlign: 'center',
      align: 'center',
      flex: 120,
      renderCell: ({ row: { id, active } }) => (
        <>
          {' '}
          {userHasDeleteRole(authInfo, 'modelo-follow-up') && (
          <IconButton
            key={`stw-active${id}`}
            value={active}
            name={`stw-active-${id}`}
            onClick={(event) => handleActivateInactivate(event, id)}
          >
            <DeleteIcon color="error" />
          </IconButton>
          )}
          <IconButton
            key={`btn-view-${id}`}
            name={`btn-view-${id}`}
            onClick={(event) => handleClickOnEditButton(event, id)}
          >
            <PageviewIcon color="primary" />
          </IconButton>
        </>
      ),
    },
  ];

  const breadcrumbs = [
    {
      link: {
        to: '/',
        name: t('com.muralis.qcx.inicio'),
      },
    },
    {
      text: {
        name: t('com.muralis.qcx.cadastros'),
      },
    },
    {
      link: {
        to: t('com.muralis.qcx.url.moduloCadastrosGerais'),
        name: t('com.muralis.qcx.gerais'),
      },
    },
    {
      text: {
        name: t('com.muralis.qcx.servico.labelPlural'),
      },
    },
    {
      text: {
        name: t('com.muralis.qcx.modelosFollowUp'),
      },
    },
  ];

  const templateProps = {
    page: {
      title: t('com.muralis.qcx.modelosFollowUp'),
      breadcrumbs,
    },
    control: {
      buttonNew: {
        description: t('com.muralis.qcx.followup.novoModeloFollowUp'),
        path: t('com.muralis.qcx.url.moduloGeraisModeloFollowUpNovo'),
        onClick: handleClickOnButtonNew,
      },
    },
    table: {
      columns,
    },
  };

  const filterProps = [
    'id',
    'sigla',
    'description',
  ];

  return (
    <QCXSimpleConsultPageTemplate
      pageProps={templateProps.page}
      controlProps={templateProps.control}
      tableProps={templateProps.table}
      tableData={modelosFollowUp}
      isLoading={isAlternativeLoading}
      multipleFilter
      filterProps={filterProps}
      authInfo={authInfo}
      requiredRoles={['modelo-follow-up']}
    />
  );
}
